(function exposeCommunityMilestoneMeter(root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.WYWCommunityMilestoneMeter = api;
    if (!root.document || root.WYWCommunityMilestoneMeterInitialized) return;
    root.WYWCommunityMilestoneMeterInitialized = true;
    const initialize = () => api.initializeCommunityMeters(root.document);
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
    root.document.addEventListener('shopify:section:load', event => api.initializeCommunityMeters(event.target));
    root.document.addEventListener('shopify:section:unload', event => {
      event.target?.querySelectorAll?.('[data-community-milestone-meter]').forEach(element => {
        element.communityMilestoneController?.destroy();
      });
    });
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function buildCommunityMilestoneMeter(browserRoot) {
  function normalizeEntry(entry) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const campaignId = typeof entry.campaignId === 'string' ? entry.campaignId.trim() : '';
    const transactionId = typeof entry.transactionId === 'string' ? entry.transactionId.trim() : '';
    const amountCents = Number(entry.amountCents);
    return campaignId && transactionId && Number.isInteger(amountCents)
      ? { campaignId, transactionId, amountCents }
      : null;
  }

  function dedupeEntries(entries) {
    const seen = new Set();
    return (Array.isArray(entries) ? entries : []).map(normalizeEntry).filter(entry => {
      if (!entry) return false;
      const key = `${entry.campaignId}\u0000${entry.transactionId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function sumCampaignCents(entries, campaignId, adjustmentCents) {
    const normalizedCampaignId = typeof campaignId === 'string' ? campaignId.trim() : '';
    const adjustment = Number(adjustmentCents);
    const initial = Number.isInteger(adjustment) ? adjustment : 0;
    const total = dedupeEntries(entries)
      .filter(entry => entry.campaignId === normalizedCampaignId)
      .reduce((sum, entry) => sum + entry.amountCents, initial);
    return Math.max(0, total);
  }

  function deriveCampaignState(totalCents, goalCents, milestones) {
    const total = Math.max(0, Number.isFinite(Number(totalCents)) ? Math.round(Number(totalCents)) : 0);
    const goal = Number(goalCents);
    const validGoal = Number.isInteger(goal) && goal > 0 ? goal : 1;
    const configured = (Array.isArray(milestones) ? milestones : [])
      .filter(item => Number.isInteger(Number(item?.thresholdCents)) && Number(item.thresholdCents) > 0)
      .map(item => ({ ...item, thresholdCents: Number(item.thresholdCents) }))
      .sort((left, right) => left.thresholdCents - right.thresholdCents);
    const nextMilestone = configured.find(item => total < item.thresholdCents) || null;
    const states = configured.map(item => ({
      ...item,
      status: total >= item.thresholdCents
        ? 'unlocked'
        : nextMilestone && item.thresholdCents === nextMilestone.thresholdCents
          ? 'next'
          : 'locked',
    }));
    const percentage = Math.min(100, Math.round(total / validGoal * 10000) / 100);
    return {
      totalCents: total,
      goalCents: validGoal,
      percentage,
      displayPercentage: Math.round(percentage),
      nextMilestone,
      remainingCents: nextMilestone ? Math.max(0, nextMilestone.thresholdCents - total) : 0,
      milestones: states,
    };
  }

  function resolveEventState(now, start, end) {
    const nowMs = new Date(now).getTime();
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (![nowMs, startMs, endMs].every(Number.isFinite) || endMs < startMs) return 'invalid';
    if (nowMs < startMs) return 'before';
    if (nowMs > endMs) return 'ended';
    return 'live';
  }

  function getRetryDelay(attempt, baseMs) {
    const safeAttempt = Math.max(0, Number.isInteger(Number(attempt)) ? Number(attempt) : 0);
    const safeBase = Math.max(1, Number.isFinite(Number(baseMs)) ? Number(baseMs) : 30000);
    return Math.min(120000, safeBase * (2 ** safeAttempt));
  }

  function parseDataPage(html, parseDocument) {
    const parser = parseDocument || (markup => {
      if (!browserRoot?.DOMParser) throw new Error('DOMParser is unavailable');
      return new browserRoot.DOMParser().parseFromString(markup, 'text/html');
    });
    const documentObject = parser(String(html));
    const root = documentObject?.querySelector?.('[data-community-milestone-page]');
    if (!root) throw new Error('Community milestone refresh returned no page data');
    const page = Number(root.dataset?.currentPage);
    const pages = Number(root.dataset?.totalPages);
    const entryCount = Number(root.dataset?.entryCount);
    if (![page, pages, entryCount].every(Number.isInteger)
      || page < 1
      || pages < 1
      || page > pages
      || entryCount < 0) {
      throw new Error('Invalid community milestone page metadata');
    }
    const entries = Array.from(root.querySelectorAll?.('[data-community-milestone-entry]') || []).map(element => (
      normalizeEntry({
        campaignId: element.dataset?.campaignId,
        transactionId: element.dataset?.transactionId,
        amountCents: element.dataset?.amountCents,
      })
    ));
    if (entries.some(entry => entry === null)) throw new Error('Invalid community milestone ledger entry');
    return { page, pages, entryCount, entries };
  }

  async function fetchAllEntryPages(fetchPage, maxPages = 20) {
    if (typeof fetchPage !== 'function') throw new Error('Community milestone page fetcher is unavailable');
    const firstPage = await fetchPage(1);
    if (!firstPage || firstPage.page !== 1) throw new Error('Incomplete community milestone refresh');
    if (!Number.isInteger(firstPage.pages) || firstPage.pages < 1) {
      throw new Error('Invalid community milestone page count');
    }
    if (firstPage.pages > maxPages) throw new Error(`Community milestone refresh exceeds ${maxPages} pages`);
    const entries = [...firstPage.entries];
    for (let page = 2; page <= firstPage.pages; page += 1) {
      const responsePage = await fetchPage(page);
      if (!responsePage
        || responsePage.page !== page
        || responsePage.pages !== firstPage.pages
        || responsePage.entryCount !== firstPage.entryCount) {
        throw new Error('Incomplete community milestone refresh');
      }
      entries.push(...responsePage.entries);
    }
    if (entries.length !== firstPage.entryCount) throw new Error('Community milestone entry count mismatch');
    return entries;
  }

  function createEntryFetcher(options = {}) {
    const fetchImpl = options.fetchImpl || browserRoot?.fetch?.bind(browserRoot);
    const routesRoot = options.routesRoot || browserRoot?.Shopify?.routes?.root || '/';
    const now = options.now || Date.now;
    const parsePage = options.parsePage || ((html) => parseDataPage(html));
    return async function fetchEntries() {
      if (typeof fetchImpl !== 'function') throw new Error('Community milestone fetch is unavailable');
      return fetchAllEntryPages(async page => {
        const parameters = new URLSearchParams({
          section_id: 'community-milestone-data',
          page: String(page),
          _community_meter: String(now()),
        });
        const separator = routesRoot.includes('?') ? '&' : '?';
        const response = await fetchImpl(`${routesRoot}${separator}${parameters}`, {
          headers: { Accept: 'text/html' },
          cache: 'no-store',
        });
        if (!response?.ok) throw new Error(`Community milestone refresh failed: ${response?.status || 'network'}`);
        return parsePage(await response.text(), page);
      }, 20);
    };
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function parseMeterConfig(root) {
    const dataset = root?.dataset || {};
    const campaignId = typeof dataset.campaignId === 'string' ? dataset.campaignId.trim() : '';
    const goalCents = Number(dataset.goalCents);
    const adjustmentCents = Number(dataset.adjustmentCents);
    const initialCents = Number(dataset.initialCents);
    const refreshSeconds = Number(dataset.refreshSeconds);
    const milestones = Array.from(root?.querySelectorAll?.('[data-community-milestone]') || []).map(element => ({
      element,
      thresholdCents: Number(element.dataset?.thresholdCents),
      title: element.dataset?.milestoneTitle || '',
    })).filter(item => Number.isInteger(item.thresholdCents) && item.thresholdCents > 0 && item.title);
    if (!campaignId
      || !Number.isInteger(goalCents)
      || goalCents <= 0
      || !Number.isInteger(adjustmentCents)
      || !Number.isInteger(initialCents)
      || !Number.isFinite(refreshSeconds)
      || refreshSeconds <= 0
      || resolveEventState(dataset.eventStart, dataset.eventStart, dataset.eventEnd) === 'invalid'
      || milestones.length === 0) {
      throw new Error('Invalid community milestone meter configuration');
    }
    return {
      campaignId,
      goalCents,
      adjustmentCents,
      initialCents: Math.max(0, initialCents),
      refreshMs: Math.round(refreshSeconds * 1000),
      eventStart: dataset.eventStart,
      eventEnd: dataset.eventEnd,
      milestones: milestones.sort((left, right) => left.thresholdCents - right.thresholdCents),
    };
  }

  function createMeterController(root, options = {}) {
    const config = parseMeterConfig(root);
    const now = options.now || (() => new Date());
    const fetchEntries = options.fetchEntries || createEntryFetcher();
    const schedule = options.schedule || ((callback, delay) => browserRoot.setTimeout(callback, delay));
    const cancelSchedule = options.cancelSchedule || (timer => browserRoot.clearTimeout(timer));
    const isVisible = options.isVisible || (() => browserRoot?.document?.visibilityState !== 'hidden');
    const reportError = options.reportError || (error => browserRoot?.console?.warn?.('[Community milestone meter]', error));
    const eventTarget = options.eventTarget || browserRoot?.document;
    let timer = null;
    let retryAttempt = 0;
    let lastGoodCents = config.initialCents;
    let listening = false;
    let destroyed = false;
    let finalized = false;

    function selectAll(selector) {
      const matches = Array.from(root.querySelectorAll?.(selector) || []);
      if (matches.length > 0) return matches;
      const match = root.querySelector?.(selector);
      return match ? [match] : [];
    }

    function setText(selector, value) {
      selectAll(selector).forEach(element => { element.textContent = value; });
    }

    function clearTimer() {
      if (timer !== null) cancelSchedule(timer);
      timer = null;
    }

    function scheduleNext(callback, delay) {
      clearTimer();
      if (destroyed) return;
      timer = schedule(callback, Math.max(0, Math.round(delay)));
    }

    function renderState(state, refreshedAt) {
      setText('[data-community-total]', formatMoney(state.totalCents));
      setText('[data-community-percent]', `${state.displayPercentage}%`);
      selectAll('[data-community-progress]').forEach(element => { element.style.width = `${state.percentage}%`; });
      selectAll('[data-community-progressbar]').forEach(element => {
        element.setAttribute('aria-valuemin', '0');
        element.setAttribute('aria-valuemax', String(config.goalCents / 100));
        element.setAttribute('aria-valuenow', String(state.totalCents / 100));
        element.setAttribute('aria-valuetext', `${formatMoney(state.totalCents)} raised of ${formatMoney(config.goalCents)}`);
      });
      if (state.nextMilestone) {
        setText('[data-community-next-threshold]', formatMoney(state.nextMilestone.thresholdCents));
        setText('[data-community-next-title]', state.nextMilestone.title);
        setText('[data-community-remaining]', `${formatMoney(state.remainingCents)} to go`);
      } else {
        setText('[data-community-next-threshold]', 'Goal reached');
        setText('[data-community-next-title]', 'Every community unlock is open');
        setText('[data-community-remaining]', 'All milestones unlocked');
      }
      state.milestones.forEach(item => {
        const configured = config.milestones.find(milestone => milestone.thresholdCents === item.thresholdCents);
        if (!configured) return;
        configured.element.dataset.status = item.status;
        const icon = configured.element.querySelector?.('[data-community-state-icon]');
        const label = configured.element.querySelector?.('[data-community-state-label]');
        if (icon) icon.textContent = item.status === 'unlocked' ? '✓' : item.status === 'next' ? '→' : '○';
        if (label) label.textContent = item.status === 'unlocked' ? 'Unlocked' : item.status === 'next' ? 'Up next' : 'Locked';
        selectAll('[data-threshold-card]').filter(card => (
          Number(card.dataset?.thresholdCard) === item.thresholdCents
        )).forEach(card => {
          card.dataset.status = item.status;
          const cardIcon = card.querySelector?.('[data-community-card-state-icon]');
          const cardLabel = card.querySelector?.('[data-community-card-state-label]');
          if (cardIcon) cardIcon.textContent = item.status === 'unlocked' ? '✓' : item.status === 'next' ? '→' : '○';
          if (cardLabel) cardLabel.textContent = item.status === 'unlocked' ? 'Unlocked' : item.status === 'next' ? 'Up next' : 'Locked';
        });
      });
      const newlyUnlocked = state.milestones.filter(item => (
        item.thresholdCents > lastGoodCents && item.thresholdCents <= state.totalCents
      ));
      if (newlyUnlocked.length > 0) {
        setText('[data-community-live-region]', `${newlyUnlocked.map(item => item.title).join(', ')} unlocked.`);
      }
      const timeText = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Phoenix',
      }).format(new Date(refreshedAt));
      setText('[data-community-freshness]', `Last updated ${timeText} MST`);
      lastGoodCents = state.totalCents;
    }

    async function refresh(refreshOptions = {}) {
      clearTimer();
      const eventState = resolveEventState(now(), config.eventStart, config.eventEnd);
      const canRefresh = eventState === 'live' || (refreshOptions.allowEnded === true && eventState === 'ended');
      if (destroyed || !isVisible() || !canRefresh) return false;
      try {
        const entries = await fetchEntries();
        const totalCents = sumCampaignCents(entries, config.campaignId, config.adjustmentCents);
        renderState(deriveCampaignState(totalCents, config.goalCents, config.milestones), now());
        if (eventState === 'ended') finalized = true;
        retryAttempt = 0;
        if (resolveEventState(now(), config.eventStart, config.eventEnd) === 'live' && isVisible()) {
          scheduleNext(refresh, config.refreshMs);
        }
        return true;
      } catch (error) {
        reportError(error);
        if (resolveEventState(now(), config.eventStart, config.eventEnd) === 'live' && isVisible()) {
          scheduleNext(refresh, getRetryDelay(retryAttempt, config.refreshMs));
          retryAttempt += 1;
        }
        return false;
      }
    }

    async function start() {
      if (destroyed) return false;
      if (!listening && eventTarget?.addEventListener) {
        eventTarget.addEventListener('visibilitychange', handleVisibilityChange);
        listening = true;
      }
      clearTimer();
      const current = now();
      const state = resolveEventState(current, config.eventStart, config.eventEnd);
      if (state === 'before') {
        scheduleNext(start, new Date(config.eventStart).getTime() - new Date(current).getTime());
        return false;
      }
      if (state === 'ended') {
        if (!isVisible() || finalized) return false;
        return refresh({ allowEnded: true });
      }
      if (state !== 'live' || !isVisible()) return false;
      return refresh();
    }

    async function handleVisibilityChange() {
      if (isVisible()) return start();
      clearTimer();
      return false;
    }

    function destroy() {
      destroyed = true;
      clearTimer();
      if (listening && eventTarget?.removeEventListener) {
        eventTarget.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      listening = false;
    }

    return { destroy, handleVisibilityChange, refresh, start };
  }

  function initializeCommunityMeters(scope) {
    const roots = [];
    if (scope?.matches?.('[data-community-milestone-meter]')) roots.push(scope);
    roots.push(...Array.from(scope?.querySelectorAll?.('[data-community-milestone-meter]') || []));
    roots.forEach(root => {
      root.communityMilestoneController?.destroy?.();
      try {
        root.communityMilestoneController = createMeterController(root);
        root.communityMilestoneController.start();
      } catch (error) {
        browserRoot?.console?.warn?.('[Community milestone meter]', error);
      }
    });
    return roots.length;
  }

  return {
    createEntryFetcher,
    createMeterController,
    dedupeEntries,
    deriveCampaignState,
    fetchAllEntryPages,
    formatMoney,
    getRetryDelay,
    initializeCommunityMeters,
    normalizeEntry,
    parseDataPage,
    resolveEventState,
    sumCampaignCents,
  };
});
